import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, GraduationCap, CheckCircle2, AlertCircle, Lock, Clock, Send, Play, RefreshCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { UniHeader } from "./_lmsShared";

export default function UniversityFinalExam() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const autosaveRef = useRef(null);
  const startedRef = useRef(null);

  const loadElig = async () => {
    setLoading(true);
    try {
      const r = await universityApi.examEligibility(enrollmentId);
      setElig(r.data);
      // If there's an open attempt, resume
      if (r.data.open_attempt_id) {
        const a = await universityApi.examAttemptGet(r.data.open_attempt_id);
        setAttempt(a.data.attempt);
        setQuestions(a.data.attempt.randomized_question_snapshot || []);
        setAnswers(a.data.attempt.answers || {});
        setIdx(a.data.attempt.current_index || 0);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Could not load eligibility"));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadElig(); }, [enrollmentId]);

  const start = async () => {
    try {
      const r = await universityApi.examStart(enrollmentId);
      setAttempt(r.data.attempt);
      setQuestions(r.data.sanitized_questions || []);
      setAnswers(r.data.attempt.answers || {});
      setIdx(0);
      startedRef.current = new Date();
      toast.success(r.data.resumed ? "Resumed in-progress exam" : "Final exam started — good luck!");
    } catch (e) {
      toast.error(formatApiError(e, "Could not start exam"));
    }
  };

  // Autosave every 5s while attempt is in progress
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress") return;
    autosaveRef.current = setInterval(async () => {
      try {
        await universityApi.examAttemptAutosave(attempt.id, { current_index: idx, answers });
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(autosaveRef.current);
  }, [attempt, idx, answers]);

  // Timer
  const timeLeftSeconds = useMemo(() => {
    if (!attempt || !attempt.started_at) return null;
    const start = new Date(attempt.started_at).getTime();
    const lim = (attempt.time_limit_minutes || 180) * 60 * 1000;
    return Math.max(0, Math.floor((start + lim - Date.now()) / 1000));
  }, [attempt]);

  const [tickN, setTickN] = useState(0);
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress") return;
    const id = setInterval(() => setTickN((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [attempt]);
  void tickN;

  const submit = async () => {
    if (!attempt) return;
    setSubmitting(true);
    try {
      const r = await universityApi.examAttemptSubmit(attempt.id, { answers });
      setResult(r.data);
      toast.success(r.data.passed ? `Passed · ${r.data.score}%` : `Submitted · ${r.data.score}% (not passing)`);
      // Refresh eligibility
      await loadElig();
      setAttempt({ ...attempt, status: r.data.status, score: r.data.score });
    } catch (e) {
      toast.error(formatApiError(e, "Submit failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Program Final Exam" back="/university/student" />
      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="uni-final-exam-page">
        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : !elig ? (
          <div className="text-slate-500">Could not load eligibility.</div>
        ) : attempt && attempt.status === "in_progress" ? (
          <ExamTaker
            attempt={attempt}
            questions={questions}
            answers={answers}
            setAnswers={setAnswers}
            idx={idx}
            setIdx={setIdx}
            timeLeftSeconds={timeLeftSeconds}
            onSubmit={submit}
            submitting={submitting}
          />
        ) : result || elig.has_passed ? (
          <ResultPanel result={result || elig.attempts_history[elig.attempts_history.length-1] || {}} elig={elig} reload={loadElig} navigate={navigate} enrollmentId={enrollmentId} />
        ) : (
          <EligibilityPanel elig={elig} onStart={start} />
        )}
      </main>
    </div>
  );
}

function EligibilityPanel({ elig, onStart }) {
  return (
    <div data-testid="uni-final-exam-eligibility">
      <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
        <GraduationCap className="w-3.5 h-3.5" />Final Exam · <span className="font-mono">{elig.level_key}</span>
      </div>
      <h1 className="font-display text-3xl text-slate-900">Program Final Exam</h1>
      <p className="text-sm text-slate-600 mt-2">
        Passing score: <b>{elig.config.passing_score}%</b> · Attempts allowed: <b>{elig.attempts_max}</b>
        {" · "}Time limit: <b>{elig.config.time_limit_minutes} minutes</b> · Questions: <b>{elig.config.default_questions}</b>
      </p>

      {/* Status tiles */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Eligibility" value={elig.eligible ? "Ready" : (elig.has_passed ? "Already passed" : "Blocked")} color={elig.eligible ? "emerald" : elig.has_passed ? "emerald" : "amber"} />
        <Tile label="Attempts used" value={`${elig.attempts_used}/${elig.attempts_max}`} />
        <Tile label="Question bank" value={`${elig.bank_count} questions`} />
        <Tile label="Status" value={elig.is_locked ? "Locked" : "Open"} color={elig.is_locked ? "rose" : "slate"} />
      </div>

      {/* Requirements checklist */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Requirements Checklist</div>
        <ul className="space-y-2">
          <Req ok={elig.courses_done} label="All required courses passed" />
          <Req ok={elig.tuition_ok} label="Tuition balance paid" />
          <Req ok={elig.bank_count > 0} label="Question bank published" />
          <Req ok={!elig.is_locked} label="Not locked from prior attempts" />
        </ul>
        {elig.missing && elig.missing.length > 0 && (
          <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="uni-final-exam-missing">
            <div className="font-medium text-amber-900 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />Outstanding requirements</div>
            <ul className="mt-2 text-xs space-y-1">
              {elig.missing.map((m, i) => (
                <li key={i} className="text-amber-900">• <span className="font-medium">{m.label}</span> — <span className="text-amber-700">{m.value}</span></li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Past attempts */}
      {elig.attempts_history && elig.attempts_history.length > 0 && (
        <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Past attempts</div>
          <ul className="space-y-2">
            {elig.attempts_history.map((a, i) => (
              <li key={a.id} className="flex items-center justify-between gap-3 text-sm">
                <span>Attempt #{a.attempt_number}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] ${a.status === "passed" ? "bg-emerald-100 text-emerald-700" : a.status === "locked" ? "bg-rose-100 text-rose-700" : a.status === "failed" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>{a.status?.toUpperCase()}</span>
                <span className="font-mono">{a.score ?? "—"}%</span>
                <span className="text-[11px] text-slate-500">{(a.submitted_at || "").slice(0, 10) || "—"}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Action */}
      <div className="mt-6 flex items-center justify-end gap-3">
        {elig.next_attempt_available_at && (
          <span className="text-xs text-amber-700">Next attempt available {new Date(elig.next_attempt_available_at).toLocaleString()}</span>
        )}
        <button
          onClick={onStart}
          disabled={!elig.eligible}
          className="rounded-xl bg-purple-900 hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed text-white h-10 px-5 text-sm inline-flex items-center gap-1.5"
          data-testid="uni-final-exam-start"
        >
          <Play className="w-4 h-4" />Start Final Exam
        </button>
      </div>
    </div>
  );
}

function ExamTaker({ attempt, questions, answers, setAnswers, idx, setIdx, timeLeftSeconds, onSubmit, submitting }) {
  const total = questions.length;
  const q = questions[idx];
  if (!q) return <div>No questions available.</div>;

  const setAnswer = (v) => setAnswers((s) => ({ ...s, [q.id]: v }));
  const answeredCount = Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== "").length;
  const mins = Math.floor((timeLeftSeconds || 0) / 60);
  const secs = (timeLeftSeconds || 0) % 60;
  const timeColor = (timeLeftSeconds || 0) < 300 ? "text-rose-700" : (timeLeftSeconds || 0) < 900 ? "text-amber-700" : "text-slate-700";

  return (
    <div data-testid="uni-final-exam-taker">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wide text-slate-500">Question <b className="text-slate-900">{idx + 1}</b> of <b className="text-slate-900">{total}</b></div>
        <div className={`text-sm inline-flex items-center gap-1.5 font-mono ${timeColor}`} data-testid="uni-final-exam-timer">
          <Clock className="w-3.5 h-3.5" />{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </div>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-5">
        <div className="h-full bg-purple-900" style={{ width: `${((idx + 1) / total) * 100}%` }} />
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-[11px] uppercase tracking-wide text-purple-900 mb-2">
          {q.question_type?.replace(/_/g, " ")} {q.course_id ? <span className="text-slate-500">· {q.course_id}</span> : null}
          {q.difficulty ? <span className="text-slate-500"> · {q.difficulty}</span> : null}
        </div>
        <div className="text-lg text-slate-900 whitespace-pre-wrap" data-testid="uni-final-exam-question-text">{q.question_text}</div>

        <div className="mt-5">
          <QuestionInput q={q} value={answers[q.id]} onChange={setAnswer} />
        </div>
      </section>

      <div className="mt-5 flex items-center justify-between">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} className="rounded-xl border border-slate-200 h-10 px-3 text-sm inline-flex items-center gap-1 disabled:opacity-40" data-testid="uni-final-exam-prev">
          <ChevronLeft className="w-3.5 h-3.5" />Previous
        </button>
        <div className="text-xs text-slate-500">Answered {answeredCount}/{total}</div>
        {idx < total - 1 ? (
          <button onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-10 px-3 text-sm inline-flex items-center gap-1" data-testid="uni-final-exam-next">
            Next<ChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button onClick={onSubmit} disabled={submitting} className="rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white h-10 px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="uni-final-exam-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Submit Exam
          </button>
        )}
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  const kind = q.question_type;
  if (kind === "multiple_choice" || kind === "true_false") {
    return (
      <ul className="space-y-2">
        {(q.answer_options || []).map((o, i) => {
          const label = (typeof o === "string" ? o : o?.label) || `Option ${i + 1}`;
          return (
            <li key={i}>
              <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${Number(value) === i ? "border-purple-900 bg-purple-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="radio" name={q.id} checked={Number(value) === i} onChange={() => onChange(i)} className="mt-1" data-testid={`uni-final-exam-opt-${i}`} />
                <span className="text-sm text-slate-900">{label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    );
  }
  if (kind === "multiple_answer") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (i) => onChange(arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i]);
    return (
      <ul className="space-y-2">
        {(q.answer_options || []).map((o, i) => {
          const label = (typeof o === "string" ? o : o?.label) || `Option ${i + 1}`;
          const checked = arr.includes(i);
          return (
            <li key={i}>
              <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${checked ? "border-purple-900 bg-purple-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(i)} className="mt-1" data-testid={`uni-final-exam-multi-${i}`} />
                <span className="text-sm text-slate-900">{label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    );
  }
  if (kind === "matching") {
    const opts = q.answer_options || []; // [{left, right_options:[...]}]
    const obj = value && typeof value === "object" ? value : {};
    return (
      <div className="space-y-2">
        {opts.map((pair, i) => (
          <div key={i} className="grid grid-cols-2 gap-3 items-center">
            <div className="text-sm text-slate-900">{pair.left || `Item ${i + 1}`}</div>
            <select
              value={obj[pair.left] || ""}
              onChange={(e) => onChange({ ...obj, [pair.left]: e.target.value })}
              className="rounded-xl border border-slate-200 h-10 px-2 text-sm"
              data-testid={`uni-final-exam-match-${i}`}
            >
              <option value="">— select —</option>
              {(pair.right_options || []).map((r, j) => <option key={j} value={r}>{r}</option>)}
            </select>
          </div>
        ))}
      </div>
    );
  }
  // short_answer / case_analysis / scenario_based
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={kind === "short_answer" ? 3 : 8}
      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
      placeholder={kind === "short_answer" ? "Type your answer…" : "Write your analysis. Be specific and reference clinical concepts."}
      data-testid="uni-final-exam-text-answer"
    />
  );
}

function ResultPanel({ result, elig, reload, navigate, enrollmentId }) {
  const passed = result?.passed || result?.status === "passed";
  const score = result?.score || 0;
  return (
    <div data-testid="uni-final-exam-result">
      <div className={`rounded-2xl border p-6 ${passed ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
        <div className="flex items-center gap-2">
          {passed ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <Lock className="w-5 h-5 text-rose-700" />}
          <span className="font-display text-xl text-slate-900">{passed ? "Passed!" : "Not yet passing"}</span>
        </div>
        <div className="mt-2 text-3xl font-display text-slate-900">{score}%</div>
        <div className="text-sm text-slate-600 mt-1">Passing required: {elig.config.passing_score}%</div>
        {result?.needs_review && (
          <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
            Some questions require instructor review. Your final score may change after grading is finalized.
          </div>
        )}
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button onClick={() => navigate("/university/student")} className="rounded-xl border border-slate-200 h-10 px-4 text-sm">Back to Portal</button>
        {!passed && !elig.is_locked && elig.attempts_remaining > 0 && (
          <button onClick={reload} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-10 px-4 text-sm inline-flex items-center gap-1.5" data-testid="uni-final-exam-retake">
            <RefreshCcw className="w-3.5 h-3.5" />Reload eligibility for retake
          </button>
        )}
      </div>
      <div className="mt-3 text-xs text-slate-500">Enrollment ID: <span className="font-mono">{enrollmentId}</span></div>
    </div>
  );
}

function Tile({ label, value, color = "slate" }) {
  const palette = {
    slate: "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
  };
  return (
    <div className={`rounded-xl border px-3 py-2 ${palette[color] || palette.slate}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function Req({ ok, label }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
      <span className={ok ? "text-slate-900" : "text-slate-500"}>{label}</span>
    </li>
  );
}
